import { supabase } from '../lib/supabase';
import { logger } from './loggingService';
import { BlueprintGeneratorService, type Blueprint } from './blueprintGeneratorService';
import { AnthropicService } from './anthropicService';
import type { Manifest } from '../types';
import toast from 'react-hot-toast';

export interface Manifest {
  id: string;
  project_id: string;
  blueprint_id: string;
  title: string;
  description: string;
  file_structure: {
    path: string;
    type: 'file' | 'directory';
    description: string;
  }[];
  dependencies: {
    name: string;
    version: string;
    purpose: string;
  }[];
  environment_variables: {
    name: string;
    description: string;
    required: boolean;
  }[];
  api_endpoints: {
    method: string;
    path: string;
    description: string;
    authentication: boolean;
  }[];
  database_schema: {
    table: string;
    columns: { name: string; type: string; constraints?: string }[];
  }[];
  deployment_steps: string[];
  testing_strategy: string;
  created_at: string;
  updated_at: string;
}

export class ManifestGeneratorService {
  static async generateFromBlueprint(blueprintId: string, userId: string): Promise<Manifest> {
    try {
      const blueprint = await BlueprintGeneratorService.getBlueprint(blueprintId);

      if (!blueprint) {
        throw new Error('Blueprint not found');
      }

      // Check if blueprint is locked (correct field check)
      if (!(blueprint as any).is_locked && blueprint.status !== 'locked' && blueprint.status !== 'frozen') {
        throw new Error('Blueprint must be locked before generating manifest');
      }

      const prompt = this.buildManifestPrompt(blueprint);

      const manifestContent = await AnthropicService.generateText(prompt, {
        maxTokens: 6000,
        temperature: 0.5,
        userId: userId,
      });

      const parsedManifest = this.parseAIResponse(manifestContent.content);

      const { data: manifest, error } = await supabase
        .from('manifests')
        .insert({
          project_id: blueprint.project_id,
          blueprint_id: blueprintId,
          title: `${blueprint.title} - Technical Manifest`,
          description: blueprint.description,
          file_structure: parsedManifest.fileStructure,
          dependencies: parsedManifest.dependencies,
          environment_variables: parsedManifest.environmentVariables,
          api_endpoints: parsedManifest.apiEndpoints,
          database_schema: parsedManifest.databaseSchema,
          deployment_steps: parsedManifest.deploymentSteps,
          testing_strategy: parsedManifest.testingStrategy,
        })
        .select()
        .single();

      if (error) throw error;

      return manifest as Manifest;
    } catch (error) {
      logger.error('Manifest generation failed', { error, blueprintId, userId });
      throw new Error(
        `Failed to generate manifest: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  private static buildManifestPrompt(blueprint: Blueprint): string {
    return `You are an expert software architect. Generate a detailed technical manifest from the following blueprint.

# Blueprint Information

**Title:** ${blueprint.title}

**Description:** ${blueprint.description}

## Architecture
- **Layers:** ${blueprint.architecture.layers.join(', ')}
- **Patterns:** ${blueprint.architecture.patterns.join(', ')}
- **Components:** ${blueprint.architecture.components.join(', ')}

## Tech Stack
- **Frontend:** ${blueprint.techStack.frontend.join(', ')}
- **Backend:** ${blueprint.techStack.backend.join(', ')}
- **Database:** ${blueprint.techStack.database.join(', ')}
- **Infrastructure:** ${blueprint.techStack.infrastructure.join(', ')}

## Standards
- **Coding:** ${blueprint.standards.coding.join(', ')}
- **Testing:** ${blueprint.standards.testing.join(', ')}
- **Documentation:** ${blueprint.standards.documentation.join(', ')}

## Phases
${blueprint.phases.map((p, i) => `${i + 1}. ${p.name} - ${p.description}\n   Deliverables: ${p.deliverables.join(', ')}`).join('\n')}

# Task

Generate a comprehensive technical manifest in the following JSON structure:

\`\`\`json
{
  "fileStructure": [
    {
      "path": "src/",
      "type": "directory",
      "description": "Source code directory"
    },
    {
      "path": "src/index.ts",
      "type": "file",
      "description": "Application entry point"
    }
  ],
  "dependencies": [
    {
      "name": "react",
      "version": "^18.0.0",
      "purpose": "UI library"
    }
  ],
  "environmentVariables": [
    {
      "name": "DATABASE_URL",
      "description": "PostgreSQL connection string",
      "required": true
    }
  ],
  "apiEndpoints": [
    {
      "method": "GET",
      "path": "/api/users",
      "description": "List all users",
      "authentication": true
    }
  ],
  "databaseSchema": [
    {
      "table": "users",
      "columns": [
        {
          "name": "id",
          "type": "UUID",
          "constraints": "PRIMARY KEY"
        }
      ]
    }
  ],
  "deploymentSteps": [
    "Install dependencies with npm install",
    "Set environment variables",
    "Run database migrations",
    "Build for production",
    "Deploy to hosting platform"
  ],
  "testingStrategy": "Unit tests with Vitest, Integration tests for API endpoints, E2E tests with Playwright for critical user flows"
}
\`\`\`

Provide ONLY the JSON object, no additional explanation.`;
  }

  private static parseAIResponse(response: string): {
    fileStructure: Manifest['file_structure'];
    dependencies: Manifest['dependencies'];
    environmentVariables: Manifest['environment_variables'];
    apiEndpoints: Manifest['api_endpoints'];
    databaseSchema: Manifest['database_schema'];
    deploymentSteps: string[];
    testingStrategy: string;
  } {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) {
        throw new Error('No JSON found in AI response');
      }

      const parsed = JSON.parse(jsonMatch[0]);

      return {
        fileStructure: parsed.fileStructure || [],
        dependencies: parsed.dependencies || [],
        environmentVariables: parsed.environmentVariables || [],
        apiEndpoints: parsed.apiEndpoints || [],
        databaseSchema: parsed.databaseSchema || [],
        deploymentSteps: parsed.deploymentSteps || [],
        testingStrategy: parsed.testingStrategy || 'No testing strategy defined',
      };
    } catch (error) {
      // Enhanced error logging with AI response excerpt
      const errorMessage = error instanceof Error ? error.message : 'Unknown parsing error';
      const responseExcerpt = response.length > 200 ? response.substring(0, 200) + '...' : response;
      
      logger.error('Failed to parse AI response, using fallback', {
        error: errorMessage,
        responseExcerpt,
        responseLength: response.length,
        timestamp: new Date().toISOString()
      });

      // Log to external service if available
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'ai_parsing_failure', {
          error_type: 'manifest_generation',
          error_message: errorMessage,
          response_length: response.length
        });
      }

      // Show user notification
      toast.error('AI response parsing failed. Using fallback manifest. Please review the generated structure.', {
        duration: 5000,
        position: 'top-right'
      });

      return {
        fileStructure: [
          { path: 'src/', type: 'directory', description: 'Source code' },
          { path: 'src/index.ts', type: 'file', description: 'Application entry point' },
          { path: 'src/components/', type: 'directory', description: 'React components' },
          { path: 'src/services/', type: 'directory', description: 'Business logic services' },
          { path: 'src/lib/', type: 'directory', description: 'Utility functions' },
        ],
        dependencies: [
          { name: 'react', version: '^18.0.0', purpose: 'UI library' },
          { name: 'typescript', version: '^5.0.0', purpose: 'Type safety' },
        ],
        environmentVariables: [
          {
            name: 'DATABASE_URL',
            description: 'Database connection string',
            required: true,
          },
          {
            name: 'API_KEY',
            description: 'External API key',
            required: true,
          },
        ],
        apiEndpoints: [
          {
            method: 'GET',
            path: '/api/data',
            description: 'Fetch data',
            authentication: true,
          },
        ],
        databaseSchema: [
          {
            table: 'users',
            columns: [
              { name: 'id', type: 'UUID', constraints: 'PRIMARY KEY' },
              { name: 'email', type: 'TEXT', constraints: 'UNIQUE NOT NULL' },
              { name: 'created_at', type: 'TIMESTAMP', constraints: 'DEFAULT NOW()' },
            ],
          },
        ],
        deploymentSteps: [
          'Install dependencies',
          'Set environment variables',
          'Run database migrations',
          'Build for production',
          'Deploy to hosting platform',
        ],
        testingStrategy: 'Unit tests, integration tests, E2E tests for critical paths',
      };
    }
  }

  static async getManifest(manifestId: string): Promise<Manifest | null> {
    const { data, error } = await supabase
      .from('manifests')
      .select('*')
      .eq('id', manifestId)
      .maybeSingle();

    if (error) {
      logger.error('Failed to fetch manifest', { error, manifestId });
      return null;
    }

    return data as Manifest;
  }
}
